import React, { useState, useEffect } from 'react';

interface SparkleProps {
  color: string;
  size: number;
  style: React.CSSProperties;
}

// Individual sparkle component
const Sparkle: React.FC<SparkleProps> = ({ color, size, style }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: 'absolute',
        animation: `sparkle-animation ${Math.random() * 2 + 4}s ease-in-out infinite`,
        ...style,
      }}
    >
      <path
        d="M80 0C80 0 84.2846 41.2925 101.496 58.504C118.707 75.7154 160 80 160 80C160 80 118.707 84.2846 101.496 101.496C84.2846 118.707 80 160 80 160C80 160 75.7154 118.707 58.504 101.496C41.2925 84.2846 0 80 0 80C0 80 41.2925 75.7154 58.504 58.504C75.7154 41.2925 80 0 80 0Z"
        fill={color}
      />
    </svg>
  );
};

interface SparklesProps {
  color?: string;
  count?: number;
}

// Main sparkles container component
const Sparkles: React.FC<SparklesProps> = ({ 
  color = '#3B82F6', // Default to blue if no color provided
  count = 8 // Reduced count for more scarcity
}) => {
  const [sparkles, setSparkles] = useState<Array<{ id: number; size: number; style: React.CSSProperties }>>([]);

  // Generate fixed sparkles once on component mount
  useEffect(() => {
    const generateSparkles = () => {
      const newSparkles = [];
      for (let i = 0; i < count; i++) {
        newSparkles.push({
          id: i,
          size: Math.random() * 8 + 4, // Slightly smaller size range
          style: {
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.2 + 0.1, // Lower opacity between 0.1-0.3
          },
        });
      }
      setSparkles(newSparkles);
    };

    generateSparkles();
    
    // No interval for regeneration - sparkles stay in fixed positions
  }, [count]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Make sure it doesn't interfere with clicks
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {sparkles.map((sparkle) => (
        <Sparkle
          key={sparkle.id}
          color={color}
          size={sparkle.size}
          style={sparkle.style}
        />
      ))}

      <style>
        {`
          @keyframes sparkle-animation {
            0% { transform: scale(0) rotate(0deg); opacity: 0; }
            15% { transform: scale(0.5) rotate(45deg); opacity: 0.3; }
            50% { transform: scale(1) rotate(90deg); opacity: 0.5; }
            85% { transform: scale(0.5) rotate(135deg); opacity: 0.3; }
            100% { transform: scale(0) rotate(180deg); opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

export default Sparkles;
